import React__default, { useState, useMemo } from 'react';
import SearchField from '../SearchField.js';
import styled from 'styled-components';
import CountryCodeHelperItem from './CountryCodeHelperItem.js';
import { Spacing, useDeviceScreenSize, StackChildren, StackChildrenSize } from '@doordash/design-language';
import Fuse from 'fuse.js';
import { useDebouncedValue, COUNTRY_CODES_TO_DIAL_CODE, AllCountryCodes } from '@doordash/utilities';
import { getFlagEmoji } from './utils/getFlagEmoji.js';
import { getIsWindows } from './utils/getIsWindows.js';
import { FieldSizeString } from '../constants.js';

function _toConsumableArray(arr) { return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray(arr) || _nonIterableSpread(); }

function _nonIterableSpread() { throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _iterableToArray(iter) { if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter); }

function _arrayWithoutHoles(arr) { if (Array.isArray(arr)) return _arrayLikeToArray(arr); }

function _slicedToArray(arr, i) { return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest(); }

function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

function _iterableToArrayLimit(arr, i) { var _i = arr && (typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"]); if (_i == null) return; var _arr = []; var _n = true; var _d = false; var _s, _e; try { for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"] != null) _i["return"](); } finally { if (_d) throw _e; } } return _arr; }

function _arrayWithHoles(arr) { if (Array.isArray(arr)) return arr; }
var fallbackLocale = 'en-US';
var fallbackCountryCodeForPlaceholder = 'US';
var helperMaxHeightPx = 400;

var getCountryEntry = function getCountryEntry(locale, countryCode) {
  var regionNamesForLocale;

  try {
    regionNamesForLocale = new Intl.DisplayNames([locale], {
      type: 'region'
    });
  } catch (e) {
    console.error("Encountered error \"".concat(e, ";\" while getting ").concat(locale, " localized country entries, falling back to ").concat(fallbackLocale, ". ").concat(locale, " may not be a valid locale."));
    regionNamesForLocale = new Intl.DisplayNames([fallbackLocale], {
      type: 'region'
    });
  }

  return {
    code: countryCode,
    displayName: regionNamesForLocale.of(countryCode),
    dialCode: "+".concat(COUNTRY_CODES_TO_DIAL_CODE[countryCode])
  };
};

var getDisplayNameOrderedCountryEntries = function getDisplayNameOrderedCountryEntries(locale) {
  var countryCodes = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : AllCountryCodes;
  var sortLocale = locale;

  try {
    ''.localeCompare('', sortLocale);
  } catch (e) {
    console.error("Encountered error \"".concat(e, ";\" while sorting country entries by locale ").concat(locale, ", falling back to ").concat(fallbackLocale, " as sort locale"));
    sortLocale = fallbackLocale;
  }

  return countryCodes.map(function (code) {
    return getCountryEntry(locale, code);
  }).sort(function (a, b) {
    return a.displayName.localeCompare(b.displayName, sortLocale);
  });
};

// ToDo: Replace custom content with Autocomplete component when it's available in Prism Web
var CountryCodeHelper = function CountryCodeHelper(_ref) {
  var locale = _ref.locale,
      preferredCountryCodes = _ref.preferredCountryCodes,
      maxCountriesDisplayed = _ref.maxCountriesDisplayed,
      onCountrySelect = _ref.onCountrySelect,
      size = _ref.size,
      minWidthPx = _ref.minWidthPx;
  var isWindows = getIsWindows();

  var _useState = useState(''),
      _useState2 = _slicedToArray(_useState, 2),
      searchFieldVal = _useState2[0],
      setSearchFieldVal = _useState2[1];

  var debouncedSearchFieldVal = useDebouncedValue(searchFieldVal, 200);

  var _useState3 = useState(0),
      _useState4 = _slicedToArray(_useState3, 2),
      highlightedItemIndex = _useState4[0],
      setHighlightedItemIndex = _useState4[1];

  var shouldHighlight = useDeviceScreenSize().isDesktopOrBigger;

  var handleSearchFieldChange = function handleSearchFieldChange(next) {
    setSearchFieldVal(next);
  };

  var handleSearchFieldKeyDown = function handleSearchFieldKeyDown(e) {
    var highlightedItem = displayedCountryEntries[highlightedItemIndex];

    if (e.key === 'Enter' && highlightedItem) {
      onCountrySelect(highlightedItem.code);
    } else if (e.key === 'ArrowDown') {
      setHighlightedItemIndex(function (p) {
        var next = p + 1;
        return next >= maxCountriesDisplayed ? 0 : next;
      });
    } else if (e.key === 'ArrowUp') {
      setHighlightedItemIndex(function (p) {
        var next = p - 1;
        return next < 0 ? maxCountriesDisplayed - 1 : next;
      });
    }
  };

  var orderedCountryEntries = useMemo(function () {
    return getDisplayNameOrderedCountryEntries(locale);
  }, [locale]);
  var searchInstance = useMemo(function () {
    // See: https://fusejs.io/api/options.html
    var options = {
      keys: [{
        name: 'code',
        weight: 2
      }, 'displayName', 'dialCode']
    };
    /**
     * Note: Does not necessarily need to take in ordered countries here since
     * results are sorted by search score anyway, but logic is simpler
     * maintaining a single localized, ordered country entry list
     */

    return new Fuse(orderedCountryEntries, options);
  }, [orderedCountryEntries]);
  var displayedCountryEntries = useMemo(function () {
    return debouncedSearchFieldVal.length ? searchInstance.search(debouncedSearchFieldVal, {
      limit: maxCountriesDisplayed
    }).map(function (fuseResult) {
      return fuseResult.item;
    }) : [].concat(_toConsumableArray(preferredCountryCodes.map(function (c) {
      return getCountryEntry(locale, c);
    })), _toConsumableArray(orderedCountryEntries.filter(function (entry) {
      return !preferredCountryCodes.includes(entry.code);
    }))).slice(0, maxCountriesDisplayed);
  }, [locale, maxCountriesDisplayed, orderedCountryEntries, preferredCountryCodes, debouncedSearchFieldVal, searchInstance]);

  var handleHelperItemClick = function handleHelperItemClick(e) {
    var clickedCountryCode = e.currentTarget.dataset.countryCode;

    if (!clickedCountryCode) {
      console.error('A country was clicked, but the data-country-code field was unset. Make sure data-country-code attribute is populated with the country code.');
      return;
    }

    onCountrySelect(clickedCountryCode);
  };

  var searchFieldPlaceholder = useMemo(function () {
    var countryEntry = getDisplayNameOrderedCountryEntries(locale, [displayedCountryEntries.length > 0 ? displayedCountryEntries[0].code : fallbackCountryCodeForPlaceholder])[0];
    return "".concat(countryEntry.displayName, " ").concat(countryEntry.dialCode);
  }, [displayedCountryEntries, locale]);
  return /*#__PURE__*/React__default.createElement(Container, {
    minWidthPx: minWidthPx
  }, /*#__PURE__*/React__default.createElement(StackChildren, {
    size: StackChildrenSize.XSmall,
    onKeyDown: handleSearchFieldKeyDown
  }, /*#__PURE__*/React__default.createElement(SearchFieldContainer, null, /*#__PURE__*/React__default.createElement(SearchField, {
    label: "Search",
    value: searchFieldVal,
    placeholder: searchFieldPlaceholder,
    onChange: handleSearchFieldChange,
    autoComplete: "off",
    isLabelHidden: true,
    size: size
  })), /*#__PURE__*/React__default.createElement(CountriesList, null, displayedCountryEntries.map(function (entry, i) {
    return /*#__PURE__*/React__default.createElement(CountryCodeHelperItem, {
      key: entry.code,
      "data-country-code": entry.code,
      countryLabel: entry.displayName,
      dialCode: entry.dialCode,
      flagEmoji: !isWindows ? getFlagEmoji(entry.code) : undefined,
      onClick: handleHelperItemClick,
      isHighlighted: shouldHighlight && highlightedItemIndex === i,
      size: FieldSizeString[size]
    });
  }))));
};

var Container = styled.div.withConfig({
  displayName: "CountryCodeHelper__Container",
  componentId: "sc-1mh93zn-0"
})(["min-width:", "px;"], function (props) {
  return props.minWidthPx;
});
var SearchFieldContainer = styled.div.withConfig({
  displayName: "CountryCodeHelper__SearchFieldContainer",
  componentId: "sc-1mh93zn-1"
})(["padding:0 ", "px;"], Spacing.Small);
var CountriesList = styled.div.withConfig({
  displayName: "CountryCodeHelper__CountriesList",
  componentId: "sc-1mh93zn-2"
})(["max-height:", "px;overflow:auto;"], helperMaxHeightPx);

export { CountryCodeHelper as default };
